import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useUser } from '../../contexts/UserContext';

/**
 * PrivateRoute - Protects routes requiring authentication and/or permissions
 *
 * @param {Object} props
 * @param {React.Component} props.as - Component to render if authorized
 * @param {string|string[]} props.requiredPermissions - Permission(s) required (any of them grants access)
 * @param {string|string[]} props.requiredAllPermissions - Permission(s) required (all must be present)
 * @param {string|string[]} props.requiredRoles - Role(s) required (any of them grants access)
 */
const PrivateRoute = ({
  as: Component,
  requiredPermissions,
  requiredAllPermissions,
  requiredRoles,
  ...props
}) => {
  const { user, hasPermission, hasAnyPermission, hasAllPermissions, hasAnyRole, refreshPermissions } = useUser();
  const location = useLocation();

  // Refresh permissions on navigation
  useEffect(() => {
    if (user) {
      refreshPermissions();
    }
  }, [location.pathname, user, refreshPermissions]);

  if (!user) {
    // Preserve full path including query string for redirect after login, so
    // email deep links survive the sign-in dance. sessionStorage is the ONLY
    // carrier (no router state: it slips past the sign-out suppression below
    // and dies on detours like verify Cancel or a fresh code request).
    //
    // Suppression: when this bounce is the result of a sign-out (explicit
    // click or the 401 interceptor force-out), do NOT stash - otherwise the
    // page the user was kicked off of becomes the post-login destination,
    // and a page that errors with 401 would pull them straight back in
    // after login (an error/logout loop). signOut() sets the marker
    // synchronously BEFORE the logged-out re-render, so there is no race.
    const signedOutAt = Number(
      window.sessionStorage.getItem('signedOutAt') || 0,
    );
    const isSignOutBounce = Date.now() - signedOutAt < 2000;
    if (!isSignOutBounce) {
      const fullPath = location.pathname + location.search;
      window.sessionStorage.setItem('postLoginRedirect', fullPath);
    }
    return <Navigate to="/sign-in" replace />;
  }

  // Redirect pending clients to processing flow, but allow any /processing* route
  if (
    user.userType === 'pending_client' &&
    !location.pathname.startsWith('/processing')
  ) {
    return <Navigate to="/processing" replace />;
  }

  // Check permission-based access (new system)
  if (requiredPermissions) {
    const perms = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];
    if (!hasAnyPermission(perms)) {
      return <Navigate to="/dashboard" />;
    }
  }

  if (requiredAllPermissions) {
    const perms = Array.isArray(requiredAllPermissions)
      ? requiredAllPermissions
      : [requiredAllPermissions];
    if (!hasAllPermissions(perms)) {
      return <Navigate to="/dashboard" />;
    }
  }

  if (requiredRoles) {
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    if (!hasAnyRole(roles)) {
      return <Navigate to="/dashboard" />;
    }
  }

  return <Component {...props} />;
};

export default PrivateRoute;
